import { AsyncPipe, DOCUMENT, Location, NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  Pipe,
  PipeTransform,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18NextModule, I18NextService, ITranslationService } from 'angular-i18next';
import { QueryRef } from 'apollo-angular';
import { BehaviorSubject, catchError, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AvatarDirective } from 'src/app/common/avatar/avatar.directive';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import {
  FollowFriendGQL,
  UnfollowFriendGQL,
} from 'src/app/_shared/dialogs/people-dialog/people-dialog.mutation.generated';
import { EllipsisTooltipDirective } from 'src/app/common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { EntityTitleComponent } from 'src/app/_shared/components/entity-title/entity-title.component';
import { CardContentFragment } from 'src/app/common/fragments/card-content.fragment.generated';
import { CardUserFragment } from 'src/app/common/fragments/card-user.fragment.generated';
import { GoogleMapService } from 'src/app/_shared/components/google-map/google-map.service';
import { GraphqlStateService } from 'src/app/common/graphql-state/graphql-state.service';
import { GtagService } from 'src/app/common/gtag/gtag.service';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { NotLoggedService } from 'src/app/common/not-logged/not-logged.service';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { SlugService } from 'src/app/common/slug/slug.service';
import { StructuredDataService } from 'src/app/common/structured-data/structured-data.service';
import { TypedTemplateDirective } from 'src/app/common/typed-template/typed-template.directive';
import { UserService } from 'src/app/common/user/user.service';
import { baseUrl, cdn, getDefaultModalSettings, stripTagsAndFormatters } from 'src/app/common/utils/helpers';
import {
  Advertisement,
  CarouselableEntityEnum,
  Follow,
  LandInfo,
  SearchComparator,
  SearchNode,
  User,
} from 'src/types/graphql';
import { AdvBannerComponent } from '../../_shared/components/adv-banner/adv-banner.component';
import { CardCarouselComponent } from '../../_shared/components/card-carousel/card-carousel.component';
import { CategoriesComponent } from '../../_shared/components/categories/categories.component';
import { ExpandableParagraphComponent } from '../../_shared/components/expandable-paragraph/expandable-paragraph.component';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { HeroComponent } from '../../_shared/components/hero/hero.component';
import { IconComponent } from '../../common/icon/icon.component';
import { LoadingComponent } from '../../_shared/components/loading/loading.component';
import { PostCardComponent } from '../../_shared/components/post-card/post-card.component';
import { UserNamePipe } from '../../common/user-name/user-name.pipe';
import { PostFragment } from '../community/community.query.generated';
import { FollowersComponent } from './followers/followers.component';
import {
  AdvGQL,
  CalendarListGQL,
  CalendarListQuery,
  CalendarListQueryVariables,
  FeaturedListGQL,
  FeaturedListQuery,
  FeaturedListQueryVariables,
  LandBySlugGQL,
  LandBySlugQuery,
  LandFragment,
  LandGalleryContentsGQL,
  LandGalleryImageFragment,
  PostListGQL,
  UserGoldListGQL,
  UserGoldListQuery,
  UserGoldListQueryVariables,
} from './land.query.generated';
import { MapDialogComponentData } from './map-dialog/map-dialog.component';
import { WeatherComponent } from './weather/weather.component';
import { SlugPipe } from '../../_shared/pipes/slug.pipe';
import { SafePipe } from '../../_shared/pipes/safe.pipe';

type Filter = {
  key: string;
  value: string;
  count: number;
  displayCount: boolean;
};

@Pipe({
  name: 'activeFilterCount',
  standalone: true,
})
export class ActiveFilterCountPipe implements PipeTransform {
  transform(filters: Filter[], key: string): number {
    return filters.find(f => f.key === key)?.count ?? 0;
  }
}

@Pipe({
  name: 'filter',
  standalone: true,
})
export class FilterPipe implements PipeTransform {
  transform(
    filters: string[],
    landInfos: {
      howReach: Partial<LandInfo>[];
      accessibility: Partial<LandInfo>[];
      ideal: Partial<LandInfo>[];
      specialDays: Partial<LandInfo>[];
      contacts: Partial<LandInfo>[];
    },
  ): string[] {
    return filters.filter(f => f === 'weatherTitle' || landInfos[f as 'howReach'].length);
  }
}

@Component({
  selector: 'sharry-land',
  standalone: true,
  imports: [
    AsyncPipe,
    AdvBannerComponent,
    CardCarouselComponent,
    HeaderComponent,
    LoadingComponent,
    FooterComponent,
    I18NextModule,
    PostCardComponent,
    MatButtonModule,
    TypedTemplateDirective,
    NgTemplateOutlet,
    TitleCasePipe,
    CategoriesComponent,
    ExpandableParagraphComponent,
    HeroComponent,
    NgClass,
    RouterLink,
    SlugPipe,
    AvatarDirective,
    IconComponent,
    UserNamePipe,
    EllipsisTooltipDirective,
    EntityTitleComponent,
    FollowersComponent,
    ActiveFilterCountPipe,
    FilterPipe,
    SafePipe,
    WeatherComponent,
  ],
  templateUrl: './land.component.html',
  styleUrl: './land.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandComponent extends BaseComponent implements AfterViewInit, OnDestroy {
  private landBySlugGQL = inject(LandBySlugGQL);
  private landGalleryContentsGQL = inject(LandGalleryContentsGQL);
  private postListGQL = inject(PostListGQL);
  private userGoldListGQL = inject(UserGoldListGQL);
  private userGoldListQuery: QueryRef<UserGoldListQuery, UserGoldListQueryVariables>;
  private featuredListGQL = inject(FeaturedListGQL);
  private featuredListQuery: QueryRef<FeaturedListQuery, FeaturedListQueryVariables>;
  private calendarListGQL = inject(CalendarListGQL);
  private calendarListQuery: QueryRef<CalendarListQuery, CalendarListQueryVariables>;
  private advGQL = inject(AdvGQL);
  private followFriendGQL: FollowFriendGQL = inject(FollowFriendGQL);
  private unfollowFriendGQL: UnfollowFriendGQL = inject(UnfollowFriendGQL);
  translateService = inject<ITranslationService>(I18NextService);
  private metadataService = inject<MetadataService>(METADATA_SERVICE);
  private location = inject(Location);
  private graphqlStateService = inject(GraphqlStateService);
  private route = inject(ActivatedRoute);
  private openDialogService = inject(OpenDialogService);
  private document = inject(DOCUMENT);
  private cd = inject(ChangeDetectorRef);
  private userService = inject(UserService);
  private gtagService = inject(GtagService);
  private slugService = inject(SlugService);
  private structuredDataService = inject(StructuredDataService);
  private notLoggedService = inject(NotLoggedService);
  private googleMapService = inject(GoogleMapService);

  typeToken: {
    title: string;
    type: 'calendar' | 'userGold' | 'featured';
    filters: Filter[];
    list: any[];
    key: string;
    busy: WritableSignal<boolean>;
  };

  backGroundImages: Record<string, any> &
    {
      title?: string;
      title$?: Observable<string>;
      subtitle: string;
      textColor: string;
    }[] = [];
  resolutions = {
    ['480']: '',
    ['600']: '',
    ['768']: '',
    ['1024']: '',
    ['1280']: '',
  };
  thumb: string;

  land$: Observable<LandFragment>;
  galleryContents$: Observable<LandGalleryImageFragment[]>;
  postList$: Observable<PostFragment[]>;
  userGoldList$: Observable<CardUserFragment[]>;
  featuredList$: Observable<CardContentFragment[]>;
  calendarList$: Observable<CardContentFragment[]>;
  advList: Observable<Partial<Advertisement>>[];
  private myFollow: BehaviorSubject<Partial<Follow>>;
  myFollow$: Observable<Partial<Follow>>;

  calendarFilters: Filter[];
  featuredFilters: Filter[];
  userGoldFilters: Filter[];
  calendarBusy = signal(false);
  featuredBusy = signal(false);
  userGoldBusy = signal(false);
  futureDate = new Date(new Date().setFullYear(new Date().getFullYear() + 999));
  currentCalendarKey: string = this.futureDate.toISOString();
  currentFeaturedKey: string = '';
  currentUserGoldKey: string = '';
  currentUsefulKey: string = '';
  headerIntersecting = true;
  meAsFollower: Partial<User>;
  onFollowBusy: boolean;

  constructor() {
    super();

    this.googleMapService.apiLoaded.pipe(takeUntilDestroyed()).subscribe(r => {
      if (!r) this.googleMapService.loadApi();
    });
    this.myFollow = new BehaviorSubject(undefined);
    this.myFollow$ = this.myFollow.asObservable();
    this.land$ = this.route.params.pipe(
      switchMap(params => {
        const state = this.graphqlStateService.getFromState<LandBySlugQuery>(this.landBySlugGQL.document, {
          slug: params.slug,
        });
        if (state) {
          return of(state);
        }
        return this.landBySlugGQL.fetch({ slug: params.slug }, { fetchPolicy: 'cache-first' });
      }),
      map(r => r.data.landBySlug),
      tap(land => {
        this.myFollow.next(land.myFollow);
        this.structuredDataService.createScript(`struct-land-${land.id}`, {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: land.title,
          image: [land.coverPicture.bestVersion.url],
          datePublished: new Date(land.deployedAt).toISOString(),
          dateModified: new Date(land.updatedAt).toISOString(),
          author: [
            {
              '@type': 'Person',
              name: 'SharryLand',
              url: `${baseUrl}${this.slugService.slug({ __typename: 'User', slug: 'sharryland' })}`,
            },
          ],
        });
        this.gtagService.pushEvent(
          {
            event: 'view_item',
            ecommerce: {
              items: [
                {
                  item_name: land?.title,
                  item_id: land?.id,
                  item_category: 'land',
                },
              ],
            },
          },
          true,
        );
        this.createMetadata(land);
        this.thumb = `${cdn}/land/${land.slug}/cover.0.preview.webp`;

        this.backGroundImages = land.galleryContents.slice(0, 4).map((_, i) => ({
          '480': `${cdn}/land/${land.slug}/cover.${i}.500.webp`,
          '600': `${cdn}/land/${land.slug}/cover.${i}.600.webp`,
          '768': `${cdn}/land/${land.slug}/cover.${i}.800.webp`,
          '1024': `${cdn}/land/${land.slug}/cover.${i}.1024.webp`,
          '1280': `${cdn}/land/${land.slug}/cover.${i}.1280.webp`,
          title: '',
          subtitle: '',
          textColor: '#fff',
        }));

        this.galleryContents$ = !land.galleryContents?.length
          ? of([])
          : this.landGalleryContentsGQL.fetch({ slug: land.slug }).pipe(map(r => r.data.landBySlug.galleryContents));
        this.postList$ = !land.postList?.length
          ? of([])
          : this.postListGQL.fetch({ slug: land.slug }).pipe(map(r => r.data.landBySlug.postList));

        if (land.userGoldList?.length) {
          this.userGoldListQuery = this.userGoldListGQL.watch(
            {
              slug: land.slug,
              limit: 8,
              skip: 0,
            },
            {
              initialFetchPolicy: 'cache-only',
              fetchPolicy: 'cache-and-network',
              refetchWritePolicy: 'merge',
            },
          );
          this.userGoldList$ = this.userGoldListQuery.valueChanges.pipe(
            map(r => r.data.landBySlug.results as CardUserFragment[]),
          );
        } else {
          this.userGoldList$ = of([]);
        }

        if (land.featuredList?.length) {
          this.featuredListQuery = this.featuredListGQL.watch(
            { slug: land.slug, limit: 8, skip: 0 },
            {
              initialFetchPolicy: 'cache-only',
              fetchPolicy: 'cache-and-network',
              refetchWritePolicy: 'merge',
            },
          );
          this.featuredList$ = this.featuredListQuery.valueChanges.pipe(
            map(r => r.data.landBySlug.results as CardContentFragment[]),
          );
        } else {
          this.featuredList$ = of([]);
        }

        if (land.calendarList?.length) {
          this.calendarListQuery = this.calendarListGQL.watch(
            { slug: land.slug, limit: 8, skip: 0 },
            {
              initialFetchPolicy: 'cache-only',
              fetchPolicy: 'cache-and-network',
              refetchWritePolicy: 'merge',
            },
          );
          this.calendarList$ = this.calendarListQuery.valueChanges.pipe(
            map(r => r.data.landBySlug.results as CardContentFragment[]),
          );
        } else {
          this.calendarList$ = of([]);
        }
        this.advList = !land.visibleAdvertisements?.length
          ? []
          : this.randomizerArray([...land.visibleAdvertisements]).map(({ id }) =>
              this.advGQL.fetch({ id }).pipe(map(r => r.data.advertisement)),
            );
        this.calendarFilters = (land.calendarListCounters || [])
          .filter(c => c.count)
          .map(c => ({
            key: c.keyword ? this.setDate(c.keyword) : this.futureDate.toISOString(),
            value: 'land.' + (c.keyword ? c.keyword : 'all'),
            count: c.count,
            displayCount: !!c.keyword,
          }));

        this.featuredFilters = (land.featuredListCounters || [])
          .filter(f => f.count)
          .map(f => ({
            key: f.keyword,
            value: f.keyword ? f.keyword : 'land.all',
            count: f.count,
            displayCount: !!f.keyword,
          }));

        this.userGoldFilters = (land.userGoldListCounters || [])
          .filter(f => f.count)
          .map(f => ({
            key: f.keyword,
            value: f.keyword ? f.keyword : 'land.all',
            count: f.count,
            displayCount: !!f.keyword,
          }));
      }),
    );
  }

  ngAfterViewInit(): void {
    if (this.isPlatformBrowser()) {
      let header;
      const timeout = setInterval(() => {
        header = this.observeHeader();
        if (header) clearInterval(timeout);
      }, 500);
    }
  }

  createMetadata(land: Partial<LandFragment>) {
    this.metadataService.setTitle(land.metaTag?.title ?? land.title);
    this.metadataService.setDescription(land.metaTag?.description ?? stripTagsAndFormatters(land.description));
    this.metadataService.setTags([
      { property: 'og:title', content: land.metaTag?.title ?? land.title },
      { property: 'og:url', content: baseUrl + this.location.path() },
      {
        property: 'og:description',
        content: land.metaTag?.description ?? stripTagsAndFormatters(land.description),
      },
      {
        property: 'og:image',
        content: cdn + '/land/' + land.slug + '/cover.thumbnail.jpg',
      },
      {
        property: 'og:image:url',
        content: cdn + '/land/' + land.slug + '/cover.thumbnail.jpg',
      },
      { property: 'og:image:width', content: '600' },
      { property: 'og:image:height', content: '600' },
      { property: 'og:image:type', content: 'image/*' },
    ]);
    this.metadataService.createCanonicalUrl(this.location.path());
  }

  onFollow(land: LandFragment) {
    const fun = () => {
      this.onFollowBusy = true;
      this.subscriptions.push(
        (this.myFollow.value
          ? this.unfollowFriendGQL
              .mutate({ id: this.myFollow.value.id })
              .pipe(map(() => ({ isFollow: false, myFollow: undefined })))
          : this.followFriendGQL
              .mutate({ input: { contentId: land.id, contentType: CarouselableEntityEnum.Land } })
              .pipe(map(r => ({ isFollow: true, myFollow: r.data.followCreate })))
        )
          .pipe(
            catchError((err: any) => {
              this.onFollowBusy = false;
              this.cd.detectChanges();
              return throwError(() => err);
            }),
          )
          .subscribe({
            next: ({ isFollow, myFollow }) => {
              this.myFollow.next(myFollow);
              this.onFavourite(isFollow ? this.userService.user : null);
              if (isFollow) {
                this.gtagService.pushEvent({
                  event: 'community_interactions',
                  event_action: 'follow',
                  land_id: this.userService?.user?.id,
                });
              }
              this.onFollowBusy = false;
              this.cd.detectChanges();
            },
          }),
      );
    };
    this.notLoggedService.execute(() => fun());
  }

  onShare(land: LandFragment) {
    const data = {
      data: {
        title: land?.title,
        content: land,
      },
    };
    this.subscriptions.push(
      from(import('src/app/_shared/dialogs/social-sharing-dialog/social-sharing-dialog.component')).subscribe({
        next: ({ SocialSharingDialogComponent }) => {
          this.openDialogService.openDialog(SocialSharingDialogComponent, {
            ...getDefaultModalSettings(),
            height: 'auto',
            ...data,
          });
        },
      }),
    );
  }

  onNext(carouselType: 'calendar' | 'userGold' | 'featured', counter: number) {
    this.carouselFetchMore(carouselType, counter);
  }

  onWheel(carouselType: 'calendar' | 'userGold' | 'featured', counter: number) {
    this.carouselFetchMore(carouselType, counter);
  }

  private async carouselFetchMore(carouselType: 'calendar' | 'userGold' | 'featured', counter = 0) {
    let queryRef: QueryRef<any, any>;
    switch (carouselType) {
      case 'calendar':
        queryRef = this.calendarListQuery;
        break;
      case 'userGold':
        queryRef = this.userGoldListQuery;
        break;
      case 'featured':
        queryRef = this.featuredListQuery;
        break;
    }
    const current = queryRef.getCurrentResult().data.landBySlug?.results;
    if (current.length < counter) {
      const variables = { ...queryRef.variables, skip: current.length };
      queryRef.fetchMore({ variables });
    }
  }

  onFavourite(user: Partial<User>) {
    this.meAsFollower = user;
    this.cd.detectChanges();
  }

  randomizerArray<T>(array: T[]) {
    let currentIndex = array.length;
    let randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }

    return array;
  }

  setDate(key: string) {
    switch (key) {
      case 'day':
        return new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
      case 'week':
        return new Date(new Date(new Date().setDate(new Date().getDate() + 7)).setHours(23, 59, 59, 999)).toISOString();
      case 'month':
        return new Date(
          new Date(new Date().setDate(new Date().getDate() + 30)).setHours(23, 59, 59, 999),
        ).toISOString();
      default:
        return new Date().toISOString();
    }
  }

  onUsefulFilterClick(filter: string) {
    this.currentUsefulKey = filter;
    this.cd.detectChanges();
  }

  onFilterClick(key: string, query: 'calendar' | 'userGold' | 'featured') {
    let queryRef: QueryRef<any, any>;
    let filter: SearchNode;
    switch (query) {
      case 'calendar':
        queryRef = this.calendarListQuery;
        this.currentCalendarKey = key;
        this.calendarBusy.set(true);
        break;
      case 'userGold':
        queryRef = this.userGoldListQuery;
        this.currentUserGoldKey = key;
        filter = {
          and: [
            {
              query: {
                field: `keyword_contents_keyword_name_${this.translateService.language}`,
                comparator: SearchComparator.In,
                values: key ? [key] : [],
              },
            },
            {
              or: [
                { query: { field: 'anonymised', comparator: SearchComparator.IsNull } },
                { query: { field: 'anonymised', comparator: SearchComparator.IsFalse } },
              ],
            },
          ],
        };
        this.userGoldBusy.set(true);
        break;
      case 'featured':
        queryRef = this.featuredListQuery;
        this.currentFeaturedKey = key;
        filter = {
          query: {
            field: `keyword_contents_keyword_name_${this.translateService.language}`,
            comparator: SearchComparator.In,
            values: key ? [key] : [],
          },
        };
        this.featuredBusy.set(true);
        break;
      default:
        break;
    }
    queryRef
      .refetch({
        date: query === 'calendar' && key ? key : undefined,
        filter,
      })
      .then(() => {
        this.calendarBusy.set(false);
        this.featuredBusy.set(false);
        this.userGoldBusy.set(false);
      });
  }

  onClickVideo(embed: string) {
    import('../../_shared/dialogs/gallery-contents-video-dialog/gallery-contents-video-dialog.component').then(
      ({ GalleryContentsVideoDialogComponent }) => {
        this.openDialogService.openDialog(GalleryContentsVideoDialogComponent, {
          height: '100vh',
          width: '100vw',
          maxHeight: '100vh',
          maxWidth: '100vw',
          minHeight: '100vh',
          minWidth: '100vw',
          restoreFocus: false,
          autoFocus: false,
          data: { embed },
        });
      },
    );
  }

  onShowGalleryChange(land?: LandFragment) {
    import('src/app/_shared/dialogs/gallery-contents-dialog/gallery-contents-dialog.component').then(
      ({ GalleryContentsDialogComponent }) => {
        this.openDialogService.openDialog(
          GalleryContentsDialogComponent,
          {
            autoFocus: false,
            restoreFocus: false,
            minHeight: '100vh',
            minWidth: '100vw',
            data: {
              title: land?.title,
              type: 'land',
              slug: land?.slug,
              index: 0,
            },
          },
          true,
        );
      },
    );
  }

  observeHeader() {
    const picture = this.document.querySelector('.land__overlay');
    const header = this.document.querySelector('.land__share-actions');
    if (!header) return null;
    const inViewport: IntersectionObserverCallback = (entries, _observer) => {
      entries.forEach(entry => {
        this.headerIntersecting = entry.isIntersecting;
        this.cd.detectChanges();
      });
    };
    const Obs = new IntersectionObserver(inViewport, { threshold: 0 });
    Obs.observe(header);
    Obs.observe(picture);
    return header;
  }

  onMapClick(land: LandFragment) {
    import('./map-dialog/map-dialog.component').then(({ MapDialogComponent }) => {
      this.openDialogService.openDialog<any, MapDialogComponentData, void>(
        MapDialogComponent,
        {
          data: {
            calendarFilters: (land.calendarListCounters || [])
              .filter(c => c.count)
              .map(f => ({
                key: f.keyword ? this.setDate(f.keyword) : this.futureDate.toISOString(),
                count: f.count,
                value: f.keyword ? f.keyword : 'all',
                checked: !f.keyword,
              })),
            featuredFilters: (land.featuredListCounters || [])
              .filter(c => c.count)
              .map(f => ({
                key: f.keyword,
                count: f.count,
                value: f.keyword ? f.keyword : 'all',
                checked: !f.keyword,
              })),
            userGoldFilters: (land.userGoldListCounters || [])
              .filter(c => c.count)
              .map(f => ({
                key: f.keyword,
                count: f.count,
                value: f.keyword ? f.keyword : 'all',
                checked: !f.keyword,
              })),
            centerCoordinates: land.centerCoordinates,
            slug: land.slug,
            zoom: land.zoom,
            layerLink: land.layerLink,
          },
          closeOnNavigation: true,
        },
        true,
      );
    });
  }

  ngOnDestroy(): void {
    this.metadataService.clean();
  }
}
